import { useMemo } from "react";
import { z } from "zod";
import {
  ApprovalCard,
  type ApprovalDecision,
  type MetadataItem,
  type SerializableApprovalCard,
} from "~/components/tool-ui/approval-card";
import type { HitlToolProps } from "./hitlTools";
import { parseToolBody } from "./parseToolBody";
import type { ToolCallEntry } from "./useTurns";

// The prefixed catalog MCP profiling tool whose result feeds this card. The
// data-prep toolkit mounts the catalog MCP under the `data_prep` prefix, so the
// tool surfaces under this name in the turn.
const PROFILE_TOOL_NAME = "data_prep_validate_and_profile_file";

// Args the backend `confirm_ingest` tool sends: identifiers only. The columns
// are never on the wire — they come from the profile result — so the model
// cannot omit or distort them.
const ConfirmIngestArgsSchema = z.object({
  dataset: z.string().min(1),
  table: z.string().min(1),
  source_file: z.string().min(1),
});

// The subset of the `validate_and_profile_file` result this card renders. Extra
// keys (validation, null_count, cardinality) are ignored; `profile` is null when
// validation failed, in which case there is nothing to ingest.
const ProfileResultSchema = z.object({
  profile: z
    .object({
      columns: z.array(
        z.object({
          name: z.string(),
          data_type: z.string(),
          description: z.string().nullish(),
        })
      ),
      num_rows: z.number().nullish(),
    })
    .nullable(),
});

interface IngestIdentifiers {
  dataset: string;
  table: string;
  sourceFile: string;
}

interface IngestProfile {
  numRows: number | null;
  columns: { name: string; dataType: string; description?: string }[];
}

function parseIdentifiers(args: string): IngestIdentifiers | null {
  const view = parseToolBody(args);
  if (view.kind !== "json" || Array.isArray(view.value)) return null;
  const parsed = ConfirmIngestArgsSchema.safeParse(view.value);
  if (!parsed.success) return null;
  return {
    dataset: parsed.data.dataset,
    table: parsed.data.table,
    sourceFile: parsed.data.source_file,
  };
}

function parseProfile(result: string | undefined): IngestProfile | null {
  if (!result) return null;
  const view = parseToolBody(result);
  if (view.kind !== "json") return null;
  const parsed = ProfileResultSchema.safeParse(view.value);
  if (!parsed.success || !parsed.data.profile) return null;
  return {
    numRows: parsed.data.profile.num_rows ?? null,
    columns: parsed.data.profile.columns.map((c) => ({
      name: c.name,
      dataType: c.data_type,
      ...(c.description ? { description: c.description } : {}),
    })),
  };
}

// Does this profile call profile the file being ingested? Correlates on the
// `source_file_uri` arg so the right columns render even if several files were
// profiled in one turn.
function profilesSource(args: string, sourceFile: string): boolean {
  const view = parseToolBody(args);
  if (view.kind !== "json" || Array.isArray(view.value)) return false;
  return (view.value as { source_file_uri?: unknown }).source_file_uri === sourceFile;
}

// Find the server profile for this ingest: prefer the profile call matching the
// source file, else the most recent profile in the turn. The last candidate
// wins so a re-profile supersedes an earlier one.
function findProfile(
  toolCalls: readonly ToolCallEntry[] | undefined,
  sourceFile: string
): IngestProfile | null {
  if (!toolCalls) return null;
  const profiled = toolCalls.filter((tc) => tc.name === PROFILE_TOOL_NAME && tc.result);
  const matched = profiled.filter((tc) => profilesSource(tc.args, sourceFile));
  const candidates = matched.length > 0 ? matched : profiled;
  for (let i = candidates.length - 1; i >= 0; i--) {
    const profile = parseProfile(candidates[i].result);
    if (profile) return profile;
  }
  return null;
}

// Last path segment of a URI for a compact display label; falls back to the full
// string when there is no segment (e.g. a bare name).
function fileName(uri: string): string {
  const last = uri.replace(/\/+$/, "").split("/").pop();
  return last && last.length > 0 ? last : uri;
}

// Assemble the approval card entirely in code from the identifiers and the
// server profile — the whole point of `confirm_ingest`. When the profile is
// absent (unusual: not correlated in the turn), the card still shows the
// identifiers so the user can act, just without the column preview.
function buildCard(
  id: string,
  { dataset, table, sourceFile }: IngestIdentifiers,
  profile: IngestProfile | null
): Omit<SerializableApprovalCard, "choice"> {
  const metadata: MetadataItem[] = [
    { key: "Dataset", value: dataset },
    { key: "Target table", value: table },
    { key: "Source file", value: fileName(sourceFile) },
  ];
  if (profile?.numRows != null) {
    metadata.push({ key: "Rows", value: profile.numRows.toLocaleString() });
  }
  if (profile) {
    metadata.push({ key: "Columns", value: String(profile.columns.length) });
  }

  return {
    id,
    title: `Ingest ${fileName(sourceFile)} → ${table}`,
    icon: "database",
    metadata,
    sections:
      profile && profile.columns.length > 0
        ? [
            {
              heading: "Columns",
              items: profile.columns.map((c) => ({
                label: c.name,
                value: c.dataType,
                ...(c.description ? { description: c.description } : {}),
              })),
            },
          ]
        : undefined,
    confirmLabel: "Ingest",
  };
}

const Placeholder = () => (
  <div className="rounded-lg border border-border bg-background px-3 py-2 text-xs text-muted-foreground">
    Preparing ingest…
  </div>
);

// Renders the deferred `confirm_ingest` call as an ApprovalCard whose columns
// are a pure function of the turn's `validate_and_profile_file` result — the
// model only decides when to pause, never what the card says. Reuses the shared
// ApprovalCard and the exact deferred-resume wiring as `ask_for_approval`.
export const ConfirmIngestCard = ({ entry, isRunning, onAnswer, turnToolCalls }: HitlToolProps) => {
  const identifiers = useMemo(() => parseIdentifiers(entry.args), [entry.args]);
  const profile = useMemo(
    () => (identifiers ? findProfile(turnToolCalls, identifiers.sourceFile) : null),
    [turnToolCalls, identifiers]
  );

  // Args not yet parseable: a placeholder while streaming, a real error once the
  // run has settled — mirroring the other HITL cards.
  if (!identifiers) {
    return isRunning ? (
      <Placeholder />
    ) : (
      <div className="rounded-lg border border-destructive/30 bg-destructive/[0.06] px-3 py-2 text-xs text-destructive">
        Couldn&apos;t load this ingest.
      </div>
    );
  }

  // Identifiers are final but the profile result hasn't landed yet — wait for it
  // rather than flashing a column-less card mid-run.
  if (!profile && isRunning) {
    return <Placeholder />;
  }

  const card = buildCard(entry.id, identifiers, profile);

  return (
    <ApprovalCard
      {...card}
      choice={entry.result as ApprovalDecision | undefined}
      onConfirm={onAnswer ? () => onAnswer(entry.id, "approved") : undefined}
      onCancel={onAnswer ? () => onAnswer(entry.id, "denied") : undefined}
    />
  );
};
